import { useAddress } from '@/hooks/useAddresses';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { formatAddressLines } from '@/components/form/AddressSelector';

interface AddressDisplayProps {
  addressId?: string;
  className?: string;
  iconPosition?: 'left' | 'right';
}

export function AddressDisplay({ addressId, className, iconPosition = 'left' }: AddressDisplayProps) {
  const { t } = useLingui();
  const { data: address, isLoading } = useAddress(addressId);

  if (!addressId) {
    return <span className={`text-muted-foreground ${className}`}>{t`address.display.noAddressSet`}</span>;
  }

  if (isLoading) {
    return <Skeleton className="h-4 w-32" />;
  }

  if (!address) {
    return <span className={`text-muted-foreground ${className}`}>{t`address.display.addressNotFound`}</span>;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {iconPosition === 'left' && <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />}
      {/* whitespace-normal overrides the nowrap a truncating table cell passes down. */}
      <span className="flex min-w-0 flex-col py-1 text-sm text-left whitespace-normal break-words">
        {formatAddressLines(address).map((line) => (
          <span key={line}>{line}</span>
        ))}
      </span>
      {iconPosition === 'right' && <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />}
    </div>
  );
}